/* eslint-disable custom/no-restricted-translation-jsx-untranslated-children-text */
import React from 'react';

import { Typography } from '@mui/material';

import { useIntl } from '@kamernet/core/Intl';
import { Locale } from '@kamernet/core/Intl/types';

import styles from './Content.module.css';
import { FAQ } from './FAQ';

interface ContentCityApartmentProps {
  cityName: string;
}

export const ContentCityApartment = ({
  cityName,
}: ContentCityApartmentProps) => {
  const { locale } = useIntl();

  return locale === Locale.en ? (
    <React.Fragment>
      <Typography variant="h1" className={styles.title}>
        {`Rental Apartments in ${cityName}, Netherlands`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Looking for apartments in ${cityName}? Find apartments for rent in ${cityName} at
        best price through Kamernet. Explore all rental apartments in ${cityName} from
        verified home owners situated at best neighborhoods. Our user-friendly
        search filters allow you to quickly find and rent apartments in ${cityName}
        that are serviced and ready to move.`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`You can also expect to find various types of apartments for rent across
        ${cityName}, but you may need to decide on what type of apartments you wish
        to live in. Apartment rent in ${cityName} can also vary depending on the
        size, bedrooms, and utilities included. Here's what you need to know
        about the different types of apartments available in ${cityName} that you can
        find on Kamernet.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        1-Bed, 2-Bed, 3-Bedroom Apartments for Rent
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Kamernet has a large selection of apartments with multiple bedrooms to
        accommodate your housing needs. Tenants can either search for one
        bedroom apartments in ${cityName} that are ideal for single working
        professionals or opt for two bedroom apartments in ${cityName} that are ideal
        for couples needing extra storage. Moreover, three bedroom apartments in
        ${cityName} allow even more space so you can set up your home office, expand
        family and accommodate guests.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Affordable and Cheap Apartments for Rent
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Finding cheap apartments in ${cityName} is not easy, but on our website you
        can find cheap apartments for rent in ${cityName} at the lowest prices.
        Kamernet is the most trusted rental site that lets you discover
        affordable living options and browse affordable apartments in ${cityName}
        that are perfect fit for your lifestyle and budget.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Utilities Included Apartments for Rent
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`If you don't want to worry about calling companies to hook up gas, water
        and electricity before your next move, then Kamernet allows you to book
        all-inclusive apartments for rent in ${cityName} with bills included. Tenants
        can use our search filters to find the best apartments in ${cityName} with
        utilities included.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Pet-Friendly Apartments for Pet Lovers
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Finding a pet-friendly apartment in ${cityName} can be challenging.
        Therefore, if you want to live with your pet in your new home, then
        Kamernet can also help you find pet friendly apartments for rent in
        ${cityName} from homeowners that are ready to welcome your pets with open
        arms.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Shared Apartments and Flatshares to Rent
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Finding a shared apartment in ${cityName} is comparatively easy as it’s a new
        trend amongst millennials for renting shared apartments in ${cityName}.
        Especially on Kamernet, you can easily find a shared flat in ${cityName} to
        share accommodation for both short-term and long-term stays.`}
      </Typography>
      <Typography variant="h4" className={styles.title}>
        {`How to find Apartments in ${cityName} with Kamernet?`}
      </Typography>
      <FAQ />
    </React.Fragment>
  ) : (
    <React.Fragment>
      <Typography variant="h1" className={styles.title}>
        {`Appartement huren in ${cityName}, Nederland`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Op zoek naar appartementen in ${cityName}? Vind appartementen te huur in
        ${cityName} voor de beste prijs via Kamernet. Bekijk alle huurappartementen in
        ${cityName} van desbetreffende huiseigenaren in de beste buurten. Met onze
        gebruiksvriendelijke zoekfilters vind je snel appartementen in ${cityName}
        die klaar zijn om te huren en in te trekken.`}
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Je kunt ook verschillende soorten appartementen te huur verwachten in
        ${cityName}, maar je moet misschien beslissen in welk type appartement je
        wilt wonen. De huurprijs van een appartement in ${cityName} kan ook variëren,
        afhankelijk van de grootte, het aantal slaapkamers en de inbegrepen
        nutsvoorzieningen. Dit is wat je moet weten over de verschillende
        soorten appartementen die beschikbaar zijn in ${cityName} en die je op
        Kamernet kunt vinden.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        1-slaapkamer, 2-slaapkamer, 3-slaapkamer appartementen te huur
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Kamernet heeft een grote selectie appartementen met meerdere slaapkamers
        om aan je woonwensen te voldoen. Huurders kunnen zoeken naar
        appartementen met één slaapkamer in ${cityName} die ideaal zijn voor
        alleenstaande werkende professionals of kiezen voor appartementen met
        twee slaapkamers in ${cityName} die ideaal zijn voor stellen die extra opslag
        nodig hebben. Bovendien bieden appartementen met drie slaapkamers in
        ${cityName} nog meer ruimte, zodat je je kantoor aan huis kunt inrichten, je
        gezin kunt uitbreiden en gasten kunt ontvangen.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Betaalbare en goedkope appartementen te huur
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Het vinden van goedkope appartementen in ${cityName} is niet eenvoudig, maar
        op onze website vind je goedkope appartementen te huur in ${cityName} tegen
        de laagste prijzen. Kamernet is de meest vertrouwde verhuursite waarmee
        je betaalbare woonopties kunt ontdekken en betaalbare appartementen in
        ${cityName} kunt bekijken die perfect passen bij jouw levensstijl en budget.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Inclusief gas, water en licht appartementen te huur
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Als je je geen zorgen wilt maken over het bellen van bedrijven om gas,
        water en elektriciteit aan te sluiten voor je volgende verhuizing, dan
        kun je bij Kamernet appartementen huren inclusief gas, water en licht in
        ${cityName}. Huurders kunnen onze zoekfilters gebruiken om de beste
        appartementen in ${cityName} te vinden met voorzieningen inbegrepen.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Huisdiervriendelijke appartementen voor dierenliefhebbers
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Het vinden van een huisdiervriendelijk appartement in ${cityName} kan een
        uitdaging zijn. Als je daarom met je huisdier in je nieuwe huis wilt
        wonen, kan Kamernet je ook helpen bij het vinden van huisdiervriendelijke
        appartementen te huur in ${cityName} van huiseigenaren die klaar staan om
        jouw huisdieren met open armen te ontvangen.`}
      </Typography>
      <Typography variant="h3" className={styles.title}>
        Gedeelde appartementen te huur
      </Typography>
      <Typography variant="body1" className={styles.paragraph}>
        {`Het vinden van een gedeeld appartement in ${cityName} is relatief eenvoudig,
        aangezien het een nieuwe trend is onder millennials om gedeelde
        appartementen te huren in ${cityName}. Vooral op Kamernet kun je gemakkelijk
        een gedeeld appartement of huisgenoot vinden in ${cityName} om een woning te
        delen voor zowel korte als lange termijn.`}
      </Typography>
      <FAQ />
    </React.Fragment>
  );
};
